// Feature gating utilities for subscriber-based access control
import {
  TIER_ONLY_FEATURES,
  subscriberTier,
  tierAllows,
  effectiveFeatures,
} from '../constants/plans';

export const FEATURES = {
  TASK_MANAGEMENT: 'task_management', // Always available
  ASSET_TRACKING: 'asset_tracking',
  ISSUE_TRACKING: 'issue_tracking',
  ADVANCED_ANALYTICS: 'advanced_analytics',
  API_ACCESS: 'api_access',
  MCP_ACCESS: 'mcp_access',
  FULL_TEXT_SEARCH: 'full_text_search',
  // Tier-only entitlements: no per-workspace flag, the tier alone decides.
  ASSIGNMENTS: 'assignments',
  INTEGRATIONS: 'integrations',
  MASS_EXPORT: 'mass_export',
  SSO: 'sso',
  CUSTOMER_PORTAL: 'customer_portal',
  CUSTOM_TEMPLATES: 'custom_templates'
};

export const FEATURE_LABELS = {
  [FEATURES.TASK_MANAGEMENT]: 'Task Management',
  [FEATURES.ASSET_TRACKING]: 'Asset Tracking',
  [FEATURES.ISSUE_TRACKING]: 'Issue Tracking',
  [FEATURES.ADVANCED_ANALYTICS]: 'Analytics Dashboard',
  [FEATURES.API_ACCESS]: 'API Access',
  [FEATURES.MCP_ACCESS]: 'MCP Access',
  [FEATURES.FULL_TEXT_SEARCH]: 'Full-Text Search',
  [FEATURES.ASSIGNMENTS]: 'Assignments',
  [FEATURES.INTEGRATIONS]: 'Integrations',
  [FEATURES.MASS_EXPORT]: 'Mass Export',
  [FEATURES.SSO]: 'Single Sign-On',
  [FEATURES.CUSTOMER_PORTAL]: 'Customer Portal',
  [FEATURES.CUSTOM_TEMPLATES]: 'Custom Templates'
};

// The one place the effective-access rule lives. Entitlement (tier) and
// activation (the workspace's `features` flag) are different things: a flag
// only counts where the tier allows it, and a tier-only entitlement needs no
// flag at all. Mirrors `effective_features` in api/app/core/plans.py.
const isFeatureOn = (subscriberData, feature) => {
  const tier = subscriberTier(subscriberData);
  if (!tierAllows(tier, feature)) return false;
  if (TIER_ONLY_FEATURES.includes(feature)) return true;
  return subscriberData.features?.[feature] === true;
};

// Check if a subscriber has access to a specific feature
export const hasFeatureAccess = (subscriberData, feature) => {
  // Task management is always available
  if (feature === FEATURES.TASK_MANAGEMENT) {
    return true;
  }

  // Check if subscriber is active
  if (!subscriberData || subscriberData.active === false) {
    return false;
  }

  // Check if subscription has expired
  if (isSubscriptionExpired(subscriberData)) {
    return false;
  }

  return isFeatureOn(subscriberData, feature);
};

// Get all available features for a subscriber
export const getAvailableFeatures = (subscriberData) => {
  const availableFeatures = [FEATURES.TASK_MANAGEMENT]; // Always available

  if (!subscriberData || subscriberData.active === false) {
    return availableFeatures;
  }

  // Check expiry
  if (isSubscriptionExpired(subscriberData)) {
    return availableFeatures;
  }

  // Add enabled features — the clamped flags plus the tier-only entitlements
  Object.entries(effectiveFeatures(subscriberData)).forEach(([feature, enabled]) => {
    if (enabled && !availableFeatures.includes(feature)) {
      availableFeatures.push(feature);
    }
  });

  return availableFeatures;
};

// The workspace's term: `expires_at`, else the trial's `trial_expires_at`, as
// the API reads it (subscription_expired). Reading `expires_at` alone kept Pro
// pages open on a lapsed trial that the API refused (#977). A Timestamp, a
// Date, milliseconds (the cached copy) or an ISO string.
const termOf = (subscriberData) => {
  const term = subscriberData?.expires_at || subscriberData?.trial_expires_at;
  if (!term) return null;
  const date = term.toDate ? term.toDate() : new Date(term);
  return Number.isNaN(date.getTime()) ? null : date;
};

// Check if subscriber subscription is expired
export const isSubscriptionExpired = (subscriberData) => {
  const expiryDate = termOf(subscriberData);
  return expiryDate ? expiryDate < new Date() : false; // No term means no expiration
};

// Check if subscriber subscription is expiring soon (within days)
export const isSubscriptionExpiringSoon = (subscriberData, days = 7) => {
  const expiryDate = termOf(subscriberData);
  if (!expiryDate) {
    return false;
  }
  const now = new Date();
  const daysUntilExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
  
  return daysUntilExpiry <= days && daysUntilExpiry > 0;
};

// Get days until subscription expires
export const getDaysUntilExpiry = (subscriberData) => {
  const expiryDate = termOf(subscriberData);
  if (!expiryDate) {
    return null; // No expiry
  }
  const now = new Date();
  return Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
};

// Create feature guard component wrapper
export const createFeatureGuard = (feature, fallbackComponent = null) => {
  return (WrappedComponent) => {
    return (props) => {
      const { subscriberData, ...restProps } = props;
      
      if (!hasFeatureAccess(subscriberData, feature)) {
        if (fallbackComponent) {
          return fallbackComponent;
        }
        
        // Default fallback UI
        return (
          <div style={{ 
            padding: '16px', 
            textAlign: 'center', 
            backgroundColor: '#f5f5f5', 
            border: '1px solid #ddd',
            borderRadius: '4px' 
          }}>
            <h4>Feature Not Available</h4>
            <p>
              {FEATURE_LABELS[feature] || 'This feature'} is not included in your current subscription.
            </p>
            <p style={{ fontSize: '14px', color: '#666' }}>
              To upgrade, use Contact TrakLog on Settings, About.
            </p>
          </div>
        );
      }
      
      return <WrappedComponent {...restProps} />;
    };
  };
};

// Navigation item filter - hide items if user doesn't have access
export const filterNavigationItems = (navigationItems, subscriberData) => {
  return navigationItems.filter(item => {
    if (item.requiredFeature) {
      return hasFeatureAccess(subscriberData, item.requiredFeature);
    }
    return true; // Show items without feature requirements
  });
};

// Check if subscriber has storage space available
export const hasStorageSpaceAvailable = (subscriberData, requiredBytes = 0) => {
  if (!subscriberData) return false;
  
  const used = subscriberData.storage_used || 0;
  const allocated = subscriberData.allocated_storage || 1073741824; // 1GB default
  
  return (used + requiredBytes) <= allocated;
};

// Get storage usage information
export const getStorageInfo = (subscriberData) => {
  if (!subscriberData) {
    return {
      used: 0,
      allocated: 1073741824,
      available: 1073741824,
      percentageUsed: 0
    };
  }
  
  const used = subscriberData.storage_used || 0;
  const allocated = subscriberData.allocated_storage || 1073741824;
  const available = Math.max(0, allocated - used);
  const percentageUsed = allocated > 0 ? (used / allocated) * 100 : 0;
  
  return {
    used,
    allocated,
    available,
    percentageUsed
  };
};

// Create a hook-like function to check multiple features at once
export const useFeatureAccess = (subscriberData) => {
  return {
    hasTaskManagement: hasFeatureAccess(subscriberData, FEATURES.TASK_MANAGEMENT),
    hasAssetTracking: hasFeatureAccess(subscriberData, FEATURES.ASSET_TRACKING),
    hasIssueTracking: hasFeatureAccess(subscriberData, FEATURES.ISSUE_TRACKING),
    hasAdvancedAnalytics: hasFeatureAccess(subscriberData, FEATURES.ADVANCED_ANALYTICS),
    hasApiAccess: hasFeatureAccess(subscriberData, FEATURES.API_ACCESS),
    hasFullTextSearch: hasFeatureAccess(subscriberData, FEATURES.FULL_TEXT_SEARCH),
    isExpired: isSubscriptionExpired(subscriberData),
    isExpiringSoon: isSubscriptionExpiringSoon(subscriberData),
    daysUntilExpiry: getDaysUntilExpiry(subscriberData),
    availableFeatures: getAvailableFeatures(subscriberData),
    storageInfo: getStorageInfo(subscriberData)
  };
};